/** tm/components/ResizeHandle.tsx — 열 너비 조정 (엑셀식 드래그 리사이즈) */
import { useState, useCallback, useEffect, useRef } from 'react';
import { C } from './ui';

interface UseResizableOptions {
  initialWidth: number;
  minWidth?: number;
  maxWidth?: number;
}

/** 패널 너비 조정 훅 — 좌측 패널에 적용 */
export function useResizable({ initialWidth, minWidth = 180, maxWidth = 600 }: UseResizableOptions) {
  const [width, setWidth] = useState(initialWidth);
  const dragging = useRef(false);
  const startX = useRef(0);
  const startW = useRef(0);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragging.current = true;
    startX.current = e.clientX;
    startW.current = width;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [width]);

  useEffect(() => {
    function onMouseMove(e: MouseEvent) {
      if (!dragging.current) return;
      const delta = e.clientX - startX.current;
      const newW = Math.max(minWidth, Math.min(maxWidth, startW.current + delta));
      setWidth(newW);
    }
    function onMouseUp() {
      if (!dragging.current) return;
      dragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [minWidth, maxWidth]);

  return { width, onMouseDown };
}

/** 리사이즈 핸들 UI — 두 패널 사이에 배치 */
export function ResizeHandle({ onMouseDown }: { onMouseDown: (e: React.MouseEvent) => void }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onMouseDown={onMouseDown}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: 6, cursor: 'col-resize', flexShrink: 0,
        background: hover ? C.accent + '44' : 'transparent',
        transition: 'background 0.15s',
        position: 'relative',
      }}
    >
      {/* 가운데 점 표시 */}
      <div style={{
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: 3, height: 24, borderRadius: 2,
        background: hover ? C.accent : C.borderDk,
        transition: 'background 0.15s',
      }} />
    </div>
  );
}
